import {
  Button,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  TextField,
} from '@flexui-io/flexui-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Building2, CircleCheck, Mail, UserRound } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ApiProblemError } from '../app/api/slider-fetch';
import { useMarketingMetadata } from '../features/marketing/marketing-metadata';
import { MarketingShell } from '../features/marketing/marketing-shell';
import { useSubmitEarlyAccessRequest } from '../generated/api/early-access/early-access';
import type { SubmitEarlyAccessRequestBodyRole } from '../generated/api/models';

export const Route = createFileRoute('/early-access')({ component: EarlyAccessPage });

const roles: SubmitEarlyAccessRequestBodyRole[] = [
  'MARKETING',
  'DESIGN',
  'PRODUCT',
  'SALES',
  'LEADERSHIP',
  'ENGINEERING',
  'OTHER',
];

const FormSchema = z.object({
  company: z.string().trim().min(1).max(160),
  email: z.email().max(320),
  marketingConsent: z.boolean(),
  name: z.string().trim().min(1).max(100),
  role: z.enum(roles),
  useCase: z.string().trim().min(10).max(2000),
});
type FormValues = z.infer<typeof FormSchema>;

function EarlyAccessPage() {
  const { i18n, t } = useTranslation();
  useMarketingMetadata(
    t('marketing.earlyAccess.metaTitle'),
    t('marketing.earlyAccess.description'),
  );
  const form = useForm<FormValues>({
    defaultValues: {
      company: '',
      email: '',
      marketingConsent: false,
      name: '',
      role: 'MARKETING',
      useCase: '',
    },
    resolver: zodResolver(FormSchema),
  });
  const mutation = useSubmitEarlyAccessRequest();
  const submittedEmail = mutation.data?.status === 201 ? mutation.data.data.email : undefined;
  const errorMessage =
    mutation.error instanceof ApiProblemError && mutation.error.code === 'RATE_LIMITED'
      ? t('marketing.earlyAccess.rateLimited')
      : t('marketing.earlyAccess.submitError');

  if (submittedEmail) {
    return (
      <MarketingShell>
        <section className="early-access-success" aria-live="polite">
          <span>
            <CircleCheck aria-hidden="true" />
          </span>
          <p className="eyebrow">{t('marketing.earlyAccess.successEyebrow')}</p>
          <h1>{t('marketing.earlyAccess.successTitle')}</h1>
          <p>{t('marketing.earlyAccess.successBody', { email: submittedEmail })}</p>
          <Link className="primary-link" to="/">
            {t('marketing.earlyAccess.backHome')}
          </Link>
        </section>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell>
      <section className="early-access-layout">
        <aside>
          <Link className="back-link" to="/">
            <ArrowLeft aria-hidden="true" className="logical-chevron" size={17} />
            {t('marketing.earlyAccess.back')}
          </Link>
          <p className="eyebrow">{t('marketing.earlyAccess.eyebrow')}</p>
          <h1>{t('marketing.earlyAccess.title')}</h1>
          <p>{t('marketing.earlyAccess.description')}</p>
          <ul>
            <li>{t('marketing.earlyAccess.benefit1')}</li>
            <li>{t('marketing.earlyAccess.benefit2')}</li>
            <li>{t('marketing.earlyAccess.benefit3')}</li>
          </ul>
        </aside>
        <div className="early-access-form-card">
          <form
            onSubmit={(event) => {
              void form.handleSubmit((values) => mutation.mutate({ data: values }))(event);
            }}
            noValidate
          >
            <TextField
              {...form.register('name')}
              dir="auto"
              placeholder={t('marketing.earlyAccess.namePlaceholder')}
              disabled={mutation.isPending}
              {...(form.formState.errors.name
                ? { helperText: t('marketing.earlyAccess.required') }
                : {})}
              label={t('marketing.earlyAccess.name')}
              leftIcon={<UserRound aria-hidden="true" />}
              status={form.formState.errors.name ? 'error' : 'default'}
            />
            <TextField
              {...form.register('email')}
              dir="ltr"
              placeholder={t('marketing.earlyAccess.emailPlaceholder')}
              disabled={mutation.isPending}
              {...(form.formState.errors.email
                ? { helperText: t('marketing.earlyAccess.invalidEmail') }
                : {})}
              label={t('marketing.earlyAccess.email')}
              leftIcon={<Mail aria-hidden="true" />}
              status={form.formState.errors.email ? 'error' : 'default'}
              type="email"
            />
            <TextField
              {...form.register('company')}
              dir="auto"
              placeholder={t('marketing.earlyAccess.companyPlaceholder')}
              disabled={mutation.isPending}
              {...(form.formState.errors.company
                ? { helperText: t('marketing.earlyAccess.required') }
                : {})}
              label={t('marketing.earlyAccess.company')}
              leftIcon={<Building2 aria-hidden="true" />}
              status={form.formState.errors.company ? 'error' : 'default'}
            />
            <Controller
              control={form.control}
              name="role"
              render={({ field }) => (
                <Select
                  dir={i18n.dir()}
                  disabled={mutation.isPending}
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger
                    aria-label={t('marketing.earlyAccess.role')}
                    label={t('marketing.earlyAccess.role')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="slider-logical-select-content">
                    {roles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {t(`marketing.earlyAccess.roles.${role}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <Textarea
              {...form.register('useCase')}
              dir="auto"
              disabled={mutation.isPending}
              {...(form.formState.errors.useCase
                ? { helperText: t('marketing.earlyAccess.useCaseHint') }
                : {})}
              label={t('marketing.earlyAccess.useCase')}
              placeholder={t('marketing.earlyAccess.useCasePlaceholder')}
              status={form.formState.errors.useCase ? 'error' : 'default'}
            />
            <Controller
              control={form.control}
              name="marketingConsent"
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  description={t('marketing.earlyAccess.consentDescription')}
                  disabled={mutation.isPending}
                  label={t('marketing.earlyAccess.consent')}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                />
              )}
            />
            {mutation.isError && (
              <p className="form-error" role="alert">
                {errorMessage}
              </p>
            )}
            <Button
              disabled={mutation.isPending}
              loading={mutation.isPending}
              size="lg"
              type="submit"
            >
              {t('marketing.requestAccess')}
            </Button>
            <p className="form-privacy">{t('marketing.earlyAccess.privacyNote')}</p>
          </form>
        </div>
      </section>
    </MarketingShell>
  );
}
